import React, {useContext, useState} from 'react';
import {createPortal} from 'react-dom';
import {IFile} from "../../ModelContracts";
import useApi from "../../hooks/useApi/useApi";
import {useQuery} from "react-query"
import {Container, Row, Col, Stack, Button, Toast, ToastContainer} from "../ui";

import './FilePreview.css';
import {TaskContext} from "../../contexts/TaskContext";

interface IProps {
	file: IFile,
	files: Array<IFile>,
	onClose: {
		(): void
	},
	onChange: {
		(file: IFile): void
	},
	onDelete: {
		(file: IFile): void
	},
	isOpen?: boolean
}

function FilePreview({file, files, onClose, onChange, onDelete, isOpen = true}: IProps) {
	const {getFile} = useApi();
	const {data, isLoading} = useQuery(file.id, () => getFile(file.path));
	const {isAdmin} = useContext(TaskContext);
	const [copied, setCopied] = useState<boolean>(false);

	const handleCloseClick = () => {
		onClose();
	};

	const handleShareClick = async () => {
		await navigator.clipboard.writeText(window.location.href);
		setCopied(true);
	};

	const handleChangeClick = (direction: number) => {
		const index = files.indexOf(file);
		const targetIndex = (index + direction + files.length) % files.length;
		const targetFile = files[targetIndex];
		onChange(targetFile);
	};

	const handleDeleteClick = () => {
		onDelete(file);
		onClose();
	};

	const isVideo = () => {
		const videoTypes = ['video/mp4', 'video/ogg', 'video/webm'];
		return videoTypes.includes(data.contentType);
	};

	const isAudio = () => {
		const audioTypes = ['audio/mpeg', 'audio/ogg', 'audio/wav'];
		return audioTypes.includes(data.contentType);
	};

	const isImage = () => {
		const imageTypes = [
			'image/png',
			'image/apng',
			'image/avif',
			'image/gif',
			'image/jpeg',
			'image/png',
			'image/svg+xml',
			'image/webp'];
		return imageTypes.includes(data.contentType);
	};

	const isPreviewAvailable = () => {
		return isImage() || isAudio() || isVideo();
	};

	const getFileExtension = () => {
		return data.name.split('.').pop();
	};

	if (!isOpen) return <></>;
	return createPortal(
		<Container fluid
		           className="fixed inset-0 bg-black/80 z-50 file-preview"
		           aria-label={`Preview ${file.name}`}>
			{!isLoading &&
                <>
                    <Row className="mt-2 flex-col-reverse md:flex-row" data-testid="Preview Header">
                        <Col xs={12} md={6}>
                            <h2 className="text-white text-xl text-center md:text-start">{data.name}</h2>
                        </Col>
                        <Col xs={12} md={6} className="mb-4 md:mb-0">
                            <Stack direction="horizontal"
                                   className="items-center justify-center md:justify-end mr-4"
                                   gap={4}>
								{
									isAdmin &&
                                    <Button variant="link"
                                            className="text-sm text-white no-underline flex items-center"
                                            onClick={handleDeleteClick}>
                                        <i className="bi bi-trash text-white mr-2"/>
                                        Delete
                                    </Button>
								}
                                <Button variant="link"
                                        className="text-sm text-white no-underline"
                                        onClick={handleShareClick}>
                                    <i className="bi bi-share text-white mr-2"/>
                                    Share
                                </Button>
                                <a
                                    className="text-sm text-white no-underline btn btn-link"
                                    href={data.path}
                                    title={`Download ${data.name}`}>
                                    <i className="bi bi-download text-white mr-2"/>
                                    Download
                                </a>
                                <Button variant="link"
                                        onClick={handleCloseClick}
                                        className="text-white close-btn flex items-center fs-0">
                                    <i className="bi bi-x text-3xl"></i>
                                    Close
                                </Button>
                            </Stack>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={2}>
							{
								files.length > 1 &&
                                <Stack className="h-full items-center justify-center">
                                    <Button className="carousel-control-prev h-1/4 static"
                                            variant="link"
                                            onClick={() => handleChangeClick(-1)}>
                                        <span className="carousel-control-prev-icon"></span>
                                        <span className="sr-only">Previous</span>
                                    </Button>
                                </Stack>
							}
                        </Col>
                        <Col xs={8}>
                            <Stack
                                aria-label="File Preview"
                                className="preview-container items-center justify-center grow-0 mt-6">
								{
									isVideo() &&
                                    <video aria-label="Video Preview" controls>
                                        <source src={data.path} type={data.contentType}/>
                                    </video>
								}
								{
									isAudio() &&
                                    <audio aria-label="Audio Preview" controls>
                                        <source src={data.path} type={data.contentType}/>
                                    </audio>
								}
								{
									isImage() &&
                                    <img src={data.path} alt={`Preview ${data.name}`} aria-label="Image Preview"/>
								}
								{
									!isPreviewAvailable() &&
                                    <Stack
                                        aria-label="No Preview"
                                        direction="vertical"
                                        className="no-preview items-center justify-center">
                                        <a href={data.path} title={`Download ${data.name}`}
                                           className="no-underline flex justify-center items-center flex-col">
                                            <i role="presentation"
                                               className={`text-center text-secondary bi bi-file-earmark bi-filetype-${getFileExtension()}`}/>
                                        </a>
                                    </Stack>
								}
                            </Stack>
                        </Col>
                        <Col xs={2}>
							{
								files.length > 1 &&
                                <Stack className="h-full items-center justify-center">
                                    <Button className="carousel-control-next h-1/4 static"
                                            variant="link"
                                            onClick={() => handleChangeClick(1)}>
                                        <span className="carousel-control-next-icon"></span>
                                        <span className="sr-only">Next</span>
                                    </Button>
                                </Stack>
							}
                        </Col>
                    </Row>
                </>
			}
			<ToastContainer position="top-center">
				<Toast show={copied}
				       onClose={() => setCopied(false)}
				       autohide
				       delay={3000}
				       bg="success"
				       className="w-full px-2">
					<Toast.Body className="text-white text-center">Copied!</Toast.Body>
				</Toast>
			</ToastContainer>
		</Container>,
		document.body
	);
}

export default FilePreview;
